import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Action } from '@interstate/components/Action';
import { ArrowPathIcon, ArrowsRightLeftIcon, ArrowTopRightOnSquareIcon, PlusIcon } from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import {
    ActionWithErrorBadge,
    ROLL_TO_LABEL,
    RollToOptionsInterstate,
    useRollToPayment,
    VehicleInfoDrawer
} from '@makemydeal/dr-dash-components';
import * as scenarioGridSelectors from '@makemydeal/dr-dash-store/selectors/scenarioGridSelectors';
import { VEHICLE_ENTITY } from '@makemydeal/dr-shared-types';

import VehiclePlaceholderSvg from '../../../assets/blankVehicle.svg';
import { AiLogoIcon } from '../../common/AiLogoIcon';
import { RepairOrderAlert } from '../../vehicleInfo/RepairOrderAlert';
import { CUSTOM_VEHICLE_LABEL, useVehicleInfo } from '../../vehicleInfo/useVehicleInfo';
import { VehicleActions, VehicleBlockRoot, VehicleDetails, VehicleHeading, VehicleThumbnail } from './ScenarioVehicleBlock.style';

const TITLE_ACTION_OVERRIDES = { size: 'lg', actionIcon: undefined, sx: { textAlign: 'left' } } as const;

export const ScenarioVehicleBlock = () => {
    const theme = useInterstateTheme();
    const vehicle = useVehicleInfo();
    const rollTo = useRollToPayment();
    const isFlexBuyDeal = useSelector(scenarioGridSelectors.isFlexBuyDeal);
    const [failedImageUrl, setFailedImageUrl] = useState<string>();

    const hasUsableImage = Boolean(vehicle.imageUrl) && vehicle.imageUrl !== failedImageUrl;

    const details = [
        vehicle.vin,
        vehicle.stockNumber && vehicle.stockLabel,
        vehicle.mileage && vehicle.mileageLabel,
        vehicle.isCustom && CUSTOM_VEHICLE_LABEL
    ].filter(Boolean);

    const getChangeVehicleIcon = () => {
        if (!vehicle.hasVehicle) {
            return <PlusIcon data-testid="add-vehicle-icon" />;
        }
        return vehicle.isDealerAISearchEnabled ? (
            <AiLogoIcon data-testid="ai-assisted-vehicle-search-logo" fontSize="14px" ariaHidden />
        ) : (
            <ArrowsRightLeftIcon />
        );
    };

    return (
        <VehicleBlockRoot data-testid="scenario-vehicle-block">
            <VehicleHeading>
                <VehicleThumbnail theme={theme}>
                    <img
                        src={hasUsableImage ? vehicle.imageUrl : VehiclePlaceholderSvg}
                        alt=""
                        data-testid="scenario-vehicle-thumbnail"
                        onError={hasUsableImage ? () => setFailedImageUrl(vehicle.imageUrl) : undefined}
                    />
                </VehicleThumbnail>
                <VehicleInfoDrawer
                    actionTitle={<span title={vehicle.title}>{vehicle.title}</span>}
                    errorBadgeHasError={vehicle.hasMissingVehicleError ? false : undefined}
                    actionOverrides={TITLE_ACTION_OVERRIDES}
                />
            </VehicleHeading>
            {details.length > 0 && (
                <VehicleDetails theme={theme} data-testid="scenario-vehicle-details">
                    {details.map((detail) => (
                        <li key={detail as string}>{detail}</li>
                    ))}
                </VehicleDetails>
            )}
            <VehicleActions>
                {vehicle.isShowVehicleSelection && (
                    <ActionWithErrorBadge entityName={VEHICLE_ENTITY} hasError={vehicle.hasMissingVehicleError}>
                        <Action
                            data-testid="btnChangeVehicle"
                            size="sm"
                            onClick={vehicle.changeVehicle}
                            actionIcon={{ icon: getChangeVehicleIcon(), start: true }}
                        >
                            {vehicle.changeVehicleLabel}
                        </Action>
                    </ActionWithErrorBadge>
                )}
                {vehicle.hasVAuto && (
                    <Action
                        data-testid="vauto-link"
                        size="sm"
                        onClick={vehicle.openInVAuto}
                        disabled={!vehicle.hasVehicle}
                        actionIcon={{ icon: <ArrowTopRightOnSquareIcon />, start: true }}
                    >
                        vAuto
                    </Action>
                )}
                <RepairOrderAlert />
                {rollTo.isAvailable && !isFlexBuyDeal && (
                    <>
                        <Action
                            data-testid="scenario-roll-to"
                            size="sm"
                            onClick={rollTo.toggle}
                            disabled={rollTo.isDisabled}
                            actionIcon={{ icon: <ArrowPathIcon />, start: true }}
                        >
                            {ROLL_TO_LABEL}
                        </Action>
                        <RollToOptionsInterstate />
                    </>
                )}
            </VehicleActions>
        </VehicleBlockRoot>
    );
};
